import { useState } from "react";
import type { FieldValues, UseFormReset } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRouteMatch } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { PAGES_QUERY_KEY } from "@/react/components/Site/Pages/hooks/useGetPage";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import type { SiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/constants";
import { useDeleteCustomTheme } from "@circle-react-shared/BuilderUI/SiteThemes/useDeleteCustomTheme";
import type { Appearance } from "@circle-react-shared/BuilderUI/Store/interfaceSlice";
import { Button } from "@circle-react-shared/uikit/Button";
import { Modal } from "@circle-react-uikit/ModalV2";
import type { DeleteCustomThemeResponse } from "../../useDeleteCustomTheme";
import { DeleteCustomThemeModalBody } from "./DeleteCustomThemeModalBody";

interface DeleteCustomThemeModalProps {
  appearance: Appearance;
  currentCanvasTheme: SiteTheme;
  reset: UseFormReset<FieldValues>;
  toggleCurrentCanvasTheme: (theme: SiteTheme) => void;
}

const DeleteCustomThemeModalComponent = ({
  currentCanvasTheme,
  toggleCurrentCanvasTheme,
  appearance,
  reset,
}: DeleteCustomThemeModalProps) => {
  const match = useRouteMatch<{ pageId: string }>(
    "/settings/pages/edit/:pageId",
  );
  const pageId = Number(match?.params.pageId);

  const queryClient = useQueryClient();

  const [newThemeId, setNewThemeId] = useState<number | null>(null);
  const modal = useModal();

  const { mutate: deleteCustomTheme, isLoading: isDeletingTheme } =
    useDeleteCustomTheme();

  const handleSubmit = () => {
    deleteCustomTheme(
      {
        themeId: currentCanvasTheme.value,
        new_theme_id: newThemeId,
        page_id: pageId,
      },
      {
        onSuccess: (data: DeleteCustomThemeResponse) => {
          reset({
            theme: data.css_variables,
          });

          void queryClient.invalidateQueries(PAGES_QUERY_KEY);

          toggleCurrentCanvasTheme({
            value: data.id,
            cssVariables: data.css_variables,
            customColors: data.custom_colors,
            name: data.theme_name,
            isCustom: data.is_custom,
          });
          setNewThemeId(null);

          void modal.hide();
        },
      },
    );
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {
        if (isDeletingTheme) return;

        void modal.hide();
      }}
    >
      <Modal.Overlay />
      <Modal.Content className="!overflow-visible">
        <Modal.Header>
          <Modal.Title size="sm" className="font-bold">
            {t(
              "builder_ui.theme_configuration.delete_custom_theme_modal.title",
            )}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton
              onClick={modal.hide}
              disabled={isDeletingTheme}
            />
          </Modal.HeaderActions>
        </Modal.Header>
        <DeleteCustomThemeModalBody
          appearance={appearance}
          currentCanvasTheme={currentCanvasTheme}
          newThemeId={newThemeId}
          setNewThemeId={setNewThemeId}
        />
        <Modal.Footer className="border-primary flex w-full justify-end gap-4 border-t">
          <Button
            variant="secondary"
            onClick={modal.hide}
            type="button"
            disabled={isDeletingTheme}
          >
            {t(
              "builder_ui.theme_configuration.delete_custom_theme_modal.secondary_cta",
            )}
          </Button>
          <Button
            variant="circle"
            disabled={isDeletingTheme}
            onClick={handleSubmit}
          >
            {t(
              "builder_ui.theme_configuration.delete_custom_theme_modal.primary_cta",
            )}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const DeleteCustomThemeModal = ModalManager.create(
  ({
    currentCanvasTheme,
    toggleCurrentCanvasTheme,
    appearance,
    reset,
  }: DeleteCustomThemeModalProps) => (
    <DeleteCustomThemeModalComponent
      currentCanvasTheme={currentCanvasTheme}
      toggleCurrentCanvasTheme={toggleCurrentCanvasTheme}
      appearance={appearance}
      reset={reset}
    />
  ),
);

export const useDeleteCustomThemeModal = () => useModal(DeleteCustomThemeModal);
